import React, { FC, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { UserRole } from "@green0meter/api-client";
import { useLocation, useNavigate } from "react-router-dom";

import { Tabs } from "@components/common";
import {
  AccountGeneralSettings,
  AccountIntegrationsSettings,
  AccountFeatures,
} from "@components/main/account";
import { Stack } from "@components/base";
import { useUserStore } from "@store/user";

type Tab = "general" | "features" | "integrations";

const ALL_TABS: Tab[] = ["general", "features", "integrations"];

const getTabFromHash = (hash: string, canManageFeatures: boolean): Tab => {
  const value = hash.replace("#", "") as Tab;

  if (!ALL_TABS.includes(value)) return "general";

  if (value === "features" && !canManageFeatures) {
    return "general";
  }

  return value;
};

const AccountSettingsPage: FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const { user } = useUserStore();
  const canManageFeatures =
    user?.role !== UserRole.Respondent &&
    user?.role !== UserRole.Viewer &&
    user?.role !== UserRole.Auditor;

  const activeTab = useMemo(
    () => getTabFromHash(location.hash, canManageFeatures),
    [location.hash, canManageFeatures],
  );

  const handleTabChange = (_: unknown, tab: Tab): void => {
    navigate({ hash: tab });
  };

  useEffect(() => {
    if (!location.hash) {
      navigate({ hash: "general" }, { replace: true });
    }
  }, []);

  return (
    <Stack spacing={2} padding={2}>
      <Tabs<Tab>
        tabs={[
          {
            title: t("settings.general"),
            path: "general",
          },
          ...(canManageFeatures
            ? [
                {
                  title: t("settings.features"),
                  path: "features" as const,
                },
              ]
            : []),
          {
            title: t("settings.integrations"),
            path: "integrations",
          },
        ]}
        activeTab={activeTab}
        onChange={handleTabChange}
      />

      {activeTab === "general" && <AccountGeneralSettings />}
      {activeTab === "features" && canManageFeatures && <AccountFeatures />}
      {activeTab === "integrations" && <AccountIntegrationsSettings />}
    </Stack>
  );
};

export default AccountSettingsPage;
